import { useEffect, useRef } from 'react'
import { Animated } from 'react-native'
import { Icon } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import type { TimeEntryStatus } from '@/types/timeTracking'

interface StatusIndicatorProps {
    status: TimeEntryStatus
}

const STATUS_ICONS: Record<TimeEntryStatus, string> = {
    active: 'timer-sand',
    pending: 'clock-outline',
    approved: 'check-circle-outline',
    rejected: 'close-circle-outline',
}

function useStatusColor(status: TimeEntryStatus) {
    const semanticColors = useSemanticColors()
    const colorMap: Record<TimeEntryStatus, string> = {
        active: semanticColors.statusActive,
        pending: semanticColors.statusPending,
        approved: semanticColors.statusApproved,
        rejected: semanticColors.statusRejected,
    }
    return colorMap[status]
}

export function StatusDot({ status }: StatusIndicatorProps) {
    const color = useStatusColor(status)
    const opacity = useRef(new Animated.Value(1)).current

    useEffect(() => {
        if (status !== 'active') {
            opacity.setValue(1)
            return
        }

        const animation = Animated.loop(
            Animated.sequence([
                Animated.timing(opacity, {
                    toValue: 0.3,
                    duration: 800,
                    useNativeDriver: true,
                }),
                Animated.timing(opacity, {
                    toValue: 1,
                    duration: 800,
                    useNativeDriver: true,
                }),
            ])
        )
        animation.start()
        return () => animation.stop()
    }, [status, opacity])

    return (
        <Animated.View style={{ opacity }}>
            <Icon source={STATUS_ICONS[status]} size={16} color={color} />
        </Animated.View>
    )
}
